import { createApiClient, apiClient } from '../../../../../api/apiClient';

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Status the backend has already evaluated for a given KPI metric, relative
 * to its target (if any). Drives the value color on the Overview KPI cards:
 *   ON_TARGET  -> primary/on-target color
 *   OFF_TARGET -> warning red
 *   NEUTRAL    -> default (no target-based coloring, e.g. plain counts)
 */
export type ASNOverviewKpiStatus = 'ON_TARGET' | 'OFF_TARGET' | 'NEUTRAL';

export interface ASNOverviewKpiMetric {
  label: string;
  value: number;
  displayValue: string;
  subLine: string;
  target?: number;
  status: ASNOverviewKpiStatus;
  dataError?: boolean;
  infoTooltip: string;
}

export interface ASNOverviewSupportingMetrics {
  totalLoads: number;
  nonAsnLoads: number;
  caseQuantity: number;
  nonAsnCaseQuantity: number;
  nonAsnSkuCount: number;
  shippingSitesInScope: number;
  sitesAt100PercentNonAsn: number;
  dataError: boolean;
}

export interface ASNOverviewKpiResponse {
  percentNonAsnLoads: ASNOverviewKpiMetric;
  nonAsnLoads: ASNOverviewKpiMetric;
  totalLoads: ASNOverviewKpiMetric;
  nonAsnSkuCount: ASNOverviewKpiMetric;
  sitesAt100PercentNonAsn: ASNOverviewKpiMetric;
  supportingMetrics: ASNOverviewSupportingMetrics;
  executionTimeMs: number;
}

/**
 * Request body for the ASN Gap Overview KPI cards.
 * All array fields are optional; null/empty = no restriction on that dimension.
 */
export interface ASNOverviewKpiRequest {
  shippingRegions: string[];
  shippingMarkets: string[];
  shippingLocationIds: string[];
  receivingLocationIds: string[];
  years: number[];
  periods: number[];
  weeks: number[];
  dateFrom: string;
  dateTo: string;
  caseQuantityMin?: number;
  caseQuantityMax?: number;
}

/** Location grain the Bottom Performing Non-ASN ranking table is grouped by —
 * driven by the Overview tab's Chart Configuration → Location Hierarchy control. */
export type ASNGapLocationHierarchy = 'REGION' | 'MARKET' | 'LOCATION';

/**
 * Request body for the ASN Gap Overview tab's "Bottom Performing Non-ASN"
 * ranking table. Extends the same filter contract as the KPI cards request
 * with the additional hierarchy/sort fields the ranking endpoint needs.
 */
export interface ASNBottomPerformingRequest extends ASNOverviewKpiRequest {
  locationHierarchy: ASNGapLocationHierarchy;
  sortColumn: string;
  sortDirection: 'ASC' | 'DESC';
}

export interface ASNBottomPerformingRow {
  rank: number;
  label: string;
  nonAsnLoads: number;
  totalLoads: number;
  percentNonAsn: number;
  status?: ASNOverviewKpiStatus;
}

export interface ASNBottomPerformingFooterMetadata {
  footerText: string;
}

export interface ASNBottomPerformingHierarchyMetadata {
  currentGrain: ASNGapLocationHierarchy;
}

export interface ASNBottomPerformingResponse {
  rows: ASNBottomPerformingRow[];
  footerMetadata: ASNBottomPerformingFooterMetadata;
  hierarchyMetadata: ASNBottomPerformingHierarchyMetadata;
}

/** Timeframe grain the "Non-ASN Load % Trend" chart is grouped by — driven
 * by the Overview tab's Chart Configuration → Timeframe control. */
export type ASNTrendTimeframeGrain = 'PERIOD' | 'WEEK' | 'YEAR';

/**
 * Request body for the ASN Gap Overview tab's "Non-ASN Load % Trend" chart.
 * Extends the same filter contract as the KPI cards request with the
 * additional timeframe-grain field the trend endpoint needs.
 */
export interface ASNNonAsnLoadTrendRequest extends ASNOverviewKpiRequest {
  timeframeGrain: ASNTrendTimeframeGrain;
}

/** Backend-evaluated direction of a point-over-point change; drives the
 * change label color. `null` means no change label should be rendered. */
export type ASNTrendPointStatus = 'IMPROVEMENT' | 'DECLINE' | null;

export interface ASNNonAsnLoadTrendPoint {
  /** Backend-formatted X-axis interval key (e.g. "10_2026" for week/period
   * grains, or a bare year for the year grain). Rendered as-is (with only
   * display formatting applied) — never recomputed or supplemented. */
  interval: string;
  percentNonAsnLoads: number;
  /** Already-formatted percentage string (e.g. "12.50%") — render directly,
   * never re-formatted. */
  formattedPercentage: string;
  /** Backend-computed point-over-point percentage-point change. `null` for
   * the first point (no prior point to compare against). Never recomputed
   * on the frontend. */
  pointChange: number | null;
  /** Backend-computed direction of `pointChange`. `null` = no change label. */
  status: ASNTrendPointStatus;
  onTarget: boolean;
}

export interface ASNNonAsnLoadTrendResponse {
  trendPoints: ASNNonAsnLoadTrendPoint[];
  targetPercentage?: number;
  totalDataPoints?: number;
  executionTimeMs?: number;
  /** Present when `trendPoints` is empty — the card renders this message
   * instead of a broken/empty chart. */
  message?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const sanitizeStringArray = (arr?: string[]): string[] => {
  if (!arr) return [];
  const cleaned = arr
    .map((item) => (typeof item === 'string' ? item.trim() : ''))
    .filter((item) => item !== '');
  return Array.from(new Set(cleaned));
};

const sanitizeNumberArray = (arr?: number[]): number[] => {
  if (!arr) return [];
  return Array.from(new Set(arr.filter((n) => Number.isFinite(n))));
};

const sanitizeCaseQuantityRange = (
  request: Pick<ASNOverviewKpiRequest, 'caseQuantityMin' | 'caseQuantityMax'>,
): Pick<ASNOverviewKpiRequest, 'caseQuantityMin' | 'caseQuantityMax'> => ({
  ...(Number.isFinite(request.caseQuantityMin) ? { caseQuantityMin: request.caseQuantityMin } : {}),
  ...(Number.isFinite(request.caseQuantityMax) ? { caseQuantityMax: request.caseQuantityMax } : {}),
});

// ─── Service Function ─────────────────────────────────────────────────────────

/**
 * Fetch the ASN Gap Overview tab's five KPI cards.
 *
 * POST /asn/overview/kpis
 *
 * @param request - Filter criteria (shipping/receiving location scope, year/period/week,
 *                  delivery date range, case quantity range). Case quantity bounds are
 *                  optional; omit min/max fields when no bound is set.
 * @returns Typed ASNOverviewKpiResponse with the five KPI metrics + supporting metrics.
 */
export async function fetchAsnOverviewKpis(
  request: ASNOverviewKpiRequest,
): Promise<ASNOverviewKpiResponse> {
  const api = createApiClient();

  const sanitizedRequest: ASNOverviewKpiRequest = {
    shippingRegions: sanitizeStringArray(request.shippingRegions),
    shippingMarkets: sanitizeStringArray(request.shippingMarkets),
    shippingLocationIds: sanitizeStringArray(request.shippingLocationIds),
    receivingLocationIds: sanitizeStringArray(request.receivingLocationIds),
    years: sanitizeNumberArray(request.years),
    periods: sanitizeNumberArray(request.periods),
    weeks: sanitizeNumberArray(request.weeks),
    dateFrom: request.dateFrom ?? '',
    dateTo: request.dateTo ?? '',
    ...sanitizeCaseQuantityRange(request),
  };

  const endpoint = '/asn/overview/kpis';

  try {
    const response = await apiClient(api, endpoint, 'POST', sanitizedRequest);
    return response as ASNOverviewKpiResponse;
  } catch (error) {
    console.error('Error fetching ASN Gap Overview KPIs:', error);
    throw error;
  }
}

/**
 * Fetch the ASN Gap Overview tab's "Bottom Performing Non-ASN" ranking table.
 *
 * POST /asn/bottom-performing-non-asn
 *
 * Shares the same filter contract as `fetchAsnOverviewKpis` plus the
 * hierarchy/sort fields the ranking endpoint needs. Re-uses the same
 * sanitize-then-POST pattern so both endpoints stay behaviorally identical.
 *
 * @param request - Filter criteria + locationHierarchy/sortColumn/sortDirection.
 * @returns Typed ASNBottomPerformingResponse (ranked rows + footer/hierarchy metadata).
 */
export async function fetchAsnBottomPerformingNonAsn(
  request: ASNBottomPerformingRequest,
): Promise<ASNBottomPerformingResponse> {
  const api = createApiClient();

  const sanitizedRequest: ASNBottomPerformingRequest = {
    shippingRegions: sanitizeStringArray(request.shippingRegions),
    shippingMarkets: sanitizeStringArray(request.shippingMarkets),
    shippingLocationIds: sanitizeStringArray(request.shippingLocationIds),
    receivingLocationIds: sanitizeStringArray(request.receivingLocationIds),
    years: sanitizeNumberArray(request.years),
    periods: sanitizeNumberArray(request.periods),
    weeks: sanitizeNumberArray(request.weeks),
    dateFrom: request.dateFrom ?? '',
    dateTo: request.dateTo ?? '',
    ...sanitizeCaseQuantityRange(request),
    locationHierarchy: request.locationHierarchy ?? 'REGION',
    sortColumn: request.sortColumn || 'NON_ASN_LOADS',
    sortDirection: request.sortDirection || 'DESC',
  };

  const endpoint = '/asn/bottom-performing-non-asn';

  try {
    const response = await apiClient(api, endpoint, 'POST', sanitizedRequest);
    return response as ASNBottomPerformingResponse;
  } catch (error) {
    console.error('Error fetching ASN Gap Bottom Performing Non-ASN:', error);
    throw error;
  }
}

/**
 * Fetch the ASN Gap Overview tab's "Non-ASN Load % Trend" chart.
 *
 * POST /asn/trend/non-asn-load-trend
 *
 * Shares the same filter contract as `fetchAsnOverviewKpis` plus the
 * `timeframeGrain` field the trend endpoint needs. Re-uses the same
 * sanitize-then-POST pattern so all three endpoints stay behaviorally
 * identical.
 *
 * @param request - Filter criteria + timeframeGrain.
 * @returns Typed ASNNonAsnLoadTrendResponse (trend points + target/empty-state metadata).
 */
export async function fetchAsnNonAsnLoadTrend(
  request: ASNNonAsnLoadTrendRequest,
): Promise<ASNNonAsnLoadTrendResponse> {
  const api = createApiClient();

  const sanitizedRequest: ASNNonAsnLoadTrendRequest = {
    shippingRegions: sanitizeStringArray(request.shippingRegions),
    shippingMarkets: sanitizeStringArray(request.shippingMarkets),
    shippingLocationIds: sanitizeStringArray(request.shippingLocationIds),
    receivingLocationIds: sanitizeStringArray(request.receivingLocationIds),
    years: sanitizeNumberArray(request.years),
    periods: sanitizeNumberArray(request.periods),
    weeks: sanitizeNumberArray(request.weeks),
    dateFrom: request.dateFrom ?? '',
    dateTo: request.dateTo ?? '',
    ...sanitizeCaseQuantityRange(request),
    timeframeGrain: request.timeframeGrain || 'PERIOD',
  };

  const endpoint = '/asn/trend/non-asn-load-trend';

  try {
    const response = await apiClient(api, endpoint, 'POST', sanitizedRequest);
    return response as ASNNonAsnLoadTrendResponse;
  } catch (error) {
    console.error('Error fetching ASN Gap Non-ASN Load % Trend:', error);
    throw error;
  }
}

/**
 * Request body for the ASN Gap "Load Details" table. Extends the same
 * filter contract as the KPI cards request with server-side pagination,
 * sorting and search fields.
 */
export interface ASNLoadDetailsRequest extends ASNOverviewKpiRequest {
  pageNumber: number;
  pageSize: number;
  /** Backend-supported sort field name, e.g. "receivedDate", "cases". */
  sortBy: string;
  sortDirection: 'ASC' | 'DESC';
  searchText: string;
}

/**
 * A single row of the "Load Details" table, as returned by the backend.
 * Every field is optional — some rows may omit shipping/receiving location
 * dimensions entirely; render missing values as blank (`value ?? ''`),
 * never guess/substitute/copy another column's value.
 */
export interface ASNLoadDetailRow {
  asnAvailable?: string | null;
  shipmentType?: string | null;
  poBoLNumber?: string | null;
  inboundDeliveryNumber?: string | null;
  conOpsNumber?: string | null;
  shippingLocationNumber?: string | null;
  shippingLocationName?: string | null;
  shippingLocationRegion?: string | null;
  shippingLocationMarket?: string | null;
  shippedDate?: string | null;
  shippedPeriod?: string | number | null;
  shippedWeek?: string | number | null;
  shippedYear?: string | number | null;
  receivedDate?: string | null;
  receivedPeriod?: string | number | null;
  receivedWeek?: string | number | null;
  receivedYear?: string | number | null;
  receivingLocationId?: string | null;
  receivingLocationName?: string | null;
  receivingRegion?: string | null;
  receivingMarket?: string | null;
  skuCount?: number | null;
  cases?: number | null;
}

export interface ASNLoadDetailsResponse {
  rows: ASNLoadDetailRow[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  sortBy: string;
  sortDirection: 'ASC' | 'DESC';
  /** Backend-provided copy — render verbatim in the table's empty state
   * whenever `hasData` is false / `rows` is empty. Never replace with a
   * hardcoded frontend string. */
  message?: string;
  hasData: boolean;
  executionTimeMs?: number;
}

/**
 * Fetch a page of the ASN Gap "Load Details" table.
 *
 * POST /asn/load-details/list
 *
 * Shares the same filter contract as `fetchAsnOverviewKpis` plus the
 * pagination/sort/search fields the list endpoint needs. Re-uses the same
 * sanitize-then-POST pattern so all four endpoints stay behaviorally
 * identical.
 *
 * @param request - Filter criteria + pageNumber/pageSize/sortBy/sortDirection/searchText.
 * @returns Typed ASNLoadDetailsResponse (rows + pagination/sort metadata).
 */
export async function fetchAsnLoadDetails(
  request: ASNLoadDetailsRequest,
): Promise<ASNLoadDetailsResponse> {
  const api = createApiClient();

  const sanitizedRequest: ASNLoadDetailsRequest = {
    shippingRegions: sanitizeStringArray(request.shippingRegions),
    shippingMarkets: sanitizeStringArray(request.shippingMarkets),
    shippingLocationIds: sanitizeStringArray(request.shippingLocationIds),
    receivingLocationIds: sanitizeStringArray(request.receivingLocationIds),
    years: sanitizeNumberArray(request.years),
    periods: sanitizeNumberArray(request.periods),
    weeks: sanitizeNumberArray(request.weeks),
    dateFrom: request.dateFrom ?? '',
    dateTo: request.dateTo ?? '',
    ...sanitizeCaseQuantityRange(request),
    pageNumber: Number.isFinite(request.pageNumber) && request.pageNumber > 0 ? request.pageNumber : 1,
    pageSize: Number.isFinite(request.pageSize) && request.pageSize > 0 ? request.pageSize : 25,
    sortBy: request.sortBy || 'receivedDate',
    sortDirection: request.sortDirection || 'DESC',
    searchText: request.searchText ?? '',
  };

  const endpoint = '/asn/load-details/list';

  try {
    const response = await apiClient(api, endpoint, 'POST', sanitizedRequest);
    return response as ASNLoadDetailsResponse;
  } catch (error) {
    console.error('Error fetching ASN Gap Load Details:', error);
    throw error;
  }
}

/**
 * Fetches every page of `POST /asn/load-details/list` for the given
 * request (same filter/sort/search contract as `fetchAsnLoadDetails`) and
 * combines all rows into a single response — used by the Load Details
 * "Export to Excel" flow, which must export every row matching the applied
 * filters/sort/search, not just the current on-screen page.
 *
 * Flow: fetch page 1 → read `totalPages` from that response → fetch pages
 * 2..totalPages sequentially → concatenate every page's `rows`. If page 1
 * already reports `totalPages <= 1` (including the no-data case), no
 * further requests are made and that single response is returned as-is
 * (`message`/`hasData` preserved verbatim so the caller can still show the
 * "no data" message without downloading an empty file).
 */
export async function fetchAsnLoadDetailsAllPages(
  request: ASNLoadDetailsRequest,
): Promise<ASNLoadDetailsResponse> {
  const firstPageRequest: ASNLoadDetailsRequest = {
    ...request,
    pageNumber: 1,
    pageSize: Number.isFinite(request.pageSize) && request.pageSize > 0 ? request.pageSize : 25,
  };

  const first = await fetchAsnLoadDetails(firstPageRequest);
  const totalPages = Math.max(1, first.totalPages || 1);

  if (totalPages === 1) {
    return first;
  }

  const allRows: ASNLoadDetailRow[] = [...first.rows];
  for (let page = 2; page <= totalPages; page++) {
    const pageResponse = await fetchAsnLoadDetails({ ...firstPageRequest, pageNumber: page });
    if (Array.isArray(pageResponse.rows) && pageResponse.rows.length > 0) {
      allRows.push(...pageResponse.rows);
    }
  }

  return {
    ...first,
    rows: allRows,
    pageNumber: 1,
    pageSize: allRows.length || first.pageSize,
    totalCount: first.totalCount || allRows.length,
    totalPages: 1,
  };
}

// ─── Location Summary / Location Details ("One Pager") ───────────────────────

/**
 * Grain (grouping) dimensions the ASN Gap Location Summary table can be
 * configured to group by via its Chart Configuration panel. A strict subset
 * of the full lane-level dimension set — matches exactly what the Chart
 * Configuration dropdown offers today (Location Type / Shipping Location /
 * Receiving Location / Warehouse System / Shipment Type are intentionally
 * not selectable grouping columns for this table).
 */
export type AsnLocationGrainKey = 'region' | 'market' | 'location' | 'locationType' | 'year' | 'period' | 'week';

/**
 * Request body for the ASN Gap "Location Summary" table.
 *
 * POST /asn/location-details
 *
 * Extends the same filter contract as the KPI cards request with the
 * Location Summary tab's own perspective/grain/sort/search/pagination
 * fields (owned independently of the shared filter bar — they change from
 * in-table interactions and the Chart Configuration panel, not the
 * Apply/Reset flow).
 */
export interface ASNLocationDetailsRequest extends ASNOverviewKpiRequest {
  perspective: 'shipping' | 'receiving' | '';
  /** Grain columns to group by, in display order — mirrors the Chart
   * Configuration panel's committed `grainSelections`. */
  grainSelections: AsnLocationGrainKey[];
  /** Backend-supported sort field — a grain key (e.g. "region") or a metric
   * key (e.g. "nonAsnLoads"). */
  sortBy: string;
  sortDirection: 'ASC' | 'DESC';
  searchQuery: string;
  /** Zero-based current page. */
  pageNumber: number;
  pageSize: number;
}

/** Pre-aggregated metric values for a single Location Summary row. */
export interface ASNLocationDetailsRowValues {
  nonAsnLoads: number;
  totalLoads: number;
  /** Non-ASN load percentage: (nonAsnLoads / totalLoads) * 100, backend-computed. */
  pctNonAsn: number;
  avgSkus: number;
  caseQuantity: number;
  /** Prior-year Non-ASN percentage, backend-computed. */
  pyPct: number;
}

/**
 * A single aggregated row of the Location Summary table, grouped by the
 * requested `grainSelections`. `grainVals` only carries entries for the
 * grain keys actually selected (see `effectiveGrainKeys` on the response) —
 * missing dimensions render as blank cells, never guessed/substituted.
 */
export interface ASNLocationDetailsRow {
  /** Stable identity for this row — use directly as the table's row key. */
  key: string;
  grainVals: Partial<Record<AsnLocationGrainKey, string>>;
  vals: ASNLocationDetailsRowValues;
  memberCount: number;
}

/**
 * Backend-computed grand total across every row matching the request's
 * filters (independent of the current page) — render directly, never
 * recalculate client-side.
 */
export interface ASNLocationDetailsGrandTotal {
  nonAsnLoads: number;
  totalLoads: number;
  pctNonAsn: number;
  avgSkus: number;
  caseQuantity: number;
}

export interface ASNLocationDetailsPagination {
  totalRowCount: number;
  totalPages: number;
  /** Zero-based current page index. */
  pageIndex: number;
  pageSize: number;
  hasMore: boolean;
}

export interface ASNLocationDetailsResponse {
  rows: ASNLocationDetailsRow[];
  /** May be null if the backend has nothing to total (e.g. a hard error
   * state) — never fall back to a client-computed total in that case. */
  grandTotal: ASNLocationDetailsGrandTotal | null;
  /** The grain keys the backend actually grouped by — may reorder/collapse
   * the requested `grainSelections` (e.g. perspective-aware reordering).
   * Drives which grain columns/headers the table renders. */
  effectiveGrainKeys: AsnLocationGrainKey[];
  pagination: ASNLocationDetailsPagination;
  executionTimeMs?: number;
}

/**
 * Fetch a page of the ASN Gap "Location Summary" table.
 *
 * POST /asn/location-details
 *
 * Shares the same filter contract as `fetchAsnOverviewKpis` plus the
 * perspective/grain/sort/search/pagination fields the location-details
 * endpoint needs. Re-uses the same sanitize-then-POST pattern as the other
 * ASN Gap endpoints in this file.
 *
 * @param request - Filter criteria + perspective/grainSelections/sortBy/
 *                  sortDirection/searchQuery/pageNumber(zero-based)/pageSize.
 * @returns Typed ASNLocationDetailsResponse (rows + grandTotal/effective
 *          grain keys/pagination metadata).
 */
export async function fetchAsnLocationDetails(
  request: ASNLocationDetailsRequest,
): Promise<ASNLocationDetailsResponse> {
  const api = createApiClient();

  const sanitizedRequest: ASNLocationDetailsRequest = {
    shippingRegions: sanitizeStringArray(request.shippingRegions),
    shippingMarkets: sanitizeStringArray(request.shippingMarkets),
    shippingLocationIds: sanitizeStringArray(request.shippingLocationIds),
    receivingLocationIds: sanitizeStringArray(request.receivingLocationIds),
    years: sanitizeNumberArray(request.years),
    periods: sanitizeNumberArray(request.periods),
    weeks: sanitizeNumberArray(request.weeks),
    dateFrom: request.dateFrom ?? '',
    dateTo: request.dateTo ?? '',
    ...sanitizeCaseQuantityRange(request),
    perspective: request.perspective || '',
    grainSelections:
      Array.isArray(request.grainSelections) && request.grainSelections.length > 0
        ? request.grainSelections
        : ['region'],
    sortBy: request.sortBy || 'nonAsnLoads',
    sortDirection: request.sortDirection || 'DESC',
    searchQuery: request.searchQuery ?? '',
    pageNumber: Number.isFinite(request.pageNumber) && request.pageNumber >= 0 ? request.pageNumber : 0,
    pageSize: Number.isFinite(request.pageSize) && request.pageSize > 0 ? request.pageSize : 50,
  };

  const endpoint = '/asn/location-details';

  try {
    const response = await apiClient(api, endpoint, 'POST', sanitizedRequest);
    return response as ASNLocationDetailsResponse;
  } catch (error) {
    console.error('Error fetching ASN Gap Location Details:', error);
    throw error;
  }
}

/**
 * Fetch ALL pages of the ASN Gap "Location Summary" table in sequence.
 * Used exclusively by the "Export to Excel" flow, which must export every row
 * matching the applied filters/sort/search regardless of the current page.
 *
 * Mirrors `fetchAsnLoadDetailsAllPages` — pages through using 0-based
 * `pageNumber` (the location-details endpoint convention) and combines all
 * rows into a single synthetic response whose `pagination` reflects the full
 * row count.
 *
 * @param request - Applied filter/sort/search request; `pageNumber` is
 *                  overridden to 0 and `pageSize` to `exportPageSize`.
 * @param exportPageSize - Rows per round trip (default 200, to minimise
 *                         the number of requests needed).
 */
export async function fetchAsnLocationDetailsAllPages(
  request: ASNLocationDetailsRequest,
  exportPageSize = 200,
): Promise<ASNLocationDetailsResponse> {
  const firstPageRequest: ASNLocationDetailsRequest = {
    ...request,
    pageNumber: 0,
    pageSize: exportPageSize,
  };
  const first = await fetchAsnLocationDetails(firstPageRequest);
  const totalPages = first.pagination?.totalPages ?? 1;

  const allRows: ASNLocationDetailsRow[] = [...(first.rows ?? [])];
  for (let page = 1; page < totalPages; page++) {
    const pageResponse = await fetchAsnLocationDetails({ ...firstPageRequest, pageNumber: page });
    allRows.push(...(pageResponse.rows ?? []));
  }

  return {
    ...first,
    rows: allRows,
    pagination: {
      ...first.pagination,
      pageIndex: 0,
      pageSize: allRows.length || exportPageSize,
      hasMore: false,
    },
  };
}

// ─── Overview tab — "ASN Loads vs Non-ASN Loads" chart ───────────────────────

/** Backend-supported sort field for the Loads vs Non-ASN chart. */
export type LoadsVsNonAsnChartSortBy = 'totalLoads' | 'nonAsnLoads' | 'pctNonAsn' | 'label' | '';

/**
 * Request body for the ASN Gap Overview tab's "ASN Loads vs Non-ASN Loads"
 * chart.
 *
 * POST /asn/loads-vs-non-asn/chart
 *
 * Extends the same filter contract as the KPI cards request. `locationHierarchy`
 * controls which grain the backend groups rows by — sent immediately whenever
 * the Chart Configuration → Location Hierarchy selection changes, so the
 * response always reflects the current grain.
 */
export interface LoadsVsNonAsnChartRequest extends ASNOverviewKpiRequest {
  sortBy: LoadsVsNonAsnChartSortBy;
  sortDirection: 'ASC' | 'DESC';
  searchQuery: string;
  /** Grain the backend should group rows by. Matches the Chart Configuration
   * → Location Hierarchy selection: Region/Unit → 'REGION', Market/POD →
   * 'MARKET', Location → 'LOCATION'. */
  locationHierarchy: ASNGapLocationHierarchy;
  parentGrain?: string;
  parentGrainVals?: Record<string, string>;
}

/** Parent-lineage identifiers carried by every row so scoped drill-down can
 * filter children by their actual ancestry (`grainVals`) instead of ever
 * matching on `label` text. A Region row only carries `region`; a Market
 * row carries `region` + `market`; a Location row carries all three. */
export interface LoadsVsNonAsnChartRowGrainVals {
  region?: string;
  market?: string;
  location?: string;
}

/**
 * A single row of the "ASN Loads vs Non-ASN Loads" chart, at one of three
 * hierarchy levels. `isAboveTarget`/`targetPct` are backend-evaluated —
 * render directly, never recompute a target comparison client-side.
 */
export interface LoadsVsNonAsnChartRow {
  label: string;
  asnLoads: number;
  nonAsnLoads: number;
  totalLoads: number;
  pctNonAsn: number;
  targetPct: number;
  isAboveTarget: boolean;
  /** 0 = Region/Unit, 1 = Market/POD, 2 = Location. */
  hierarchyLevel: 0 | 1 | 2;
  grainVals: LoadsVsNonAsnChartRowGrainVals;
  parentLabel?: string;
  parentGrainVals?: LoadsVsNonAsnChartRowGrainVals;
}

/** Backend-computed aggregate across all rows for the current filter scope.
 * Always use this for the PBNA Total bar — never recompute from visible rows. */
export interface LoadsVsNonAsnChartGrandTotal {
  asnLoads: number;
  nonAsnLoads: number;
  totalLoads: number;
  /** Weighted non-ASN percentage: (nonAsnLoads / totalLoads) * 100, backend-computed. */
  pctNonAsn: number;
  targetPct: number;
  isAboveTarget: boolean;
}

export interface LoadsVsNonAsnChartResponse {
  /** Rows for the currently-requested `locationHierarchy` grain only —
   * stored as-is; each row's own `hierarchyLevel` reflects that grain
   * (0 = Region/Unit, 1 = Market/POD, 2 = Location). In-chart drill-down
   * (a bar click) is resolved client-side against this response by scoping
   * on `grainVals`; a Chart Configuration → Location Hierarchy change
   * re-fetches with the new `locationHierarchy` instead. */
  rows: LoadsVsNonAsnChartRow[];
  /** Filter-scoped aggregate totals. Use directly for the PBNA Total bar. */
  grandTotal?: LoadsVsNonAsnChartGrandTotal;
  executionTimeMs?: number;
  /** Present when `rows` is empty — render instead of a blank chart. */
  message?: string;
}

/**
 * Fetch the ASN Gap Overview tab's "ASN Loads vs Non-ASN Loads" chart.
 *
 * POST /asn/loads-vs-non-asn/chart
 *
 * Shares the same filter contract as `fetchAsnOverviewKpis` plus the
 * chart's own sort/search fields. Re-uses the same sanitize-then-POST
 * pattern as the other ASN Gap endpoints in this file.
 *
 * @param request - Filter criteria + sortBy/sortDirection/searchQuery.
 * @returns Typed LoadsVsNonAsnChartResponse (the complete flat rows array).
 */
export async function fetchLoadsVsNonAsnChart(
  request: LoadsVsNonAsnChartRequest,
): Promise<LoadsVsNonAsnChartResponse> {
  const api = createApiClient();

  const sanitizedRequest: LoadsVsNonAsnChartRequest = {
    shippingRegions: sanitizeStringArray(request.shippingRegions),
    shippingMarkets: sanitizeStringArray(request.shippingMarkets),
    shippingLocationIds: sanitizeStringArray(request.shippingLocationIds),
    receivingLocationIds: sanitizeStringArray(request.receivingLocationIds),
    years: sanitizeNumberArray(request.years),
    periods: sanitizeNumberArray(request.periods),
    weeks: sanitizeNumberArray(request.weeks),
    dateFrom: request.dateFrom ?? '',
    dateTo: request.dateTo ?? '',
    ...sanitizeCaseQuantityRange(request),
    sortBy: request.sortBy ?? '',
    sortDirection: request.sortDirection || 'DESC',
    searchQuery: request.searchQuery ?? '',
    locationHierarchy: request.locationHierarchy || 'REGION',
    parentGrain: request.parentGrain,
    parentGrainVals: request.parentGrainVals,
  };

  const endpoint = '/asn/loads-vs-non-asn/chart';

  try {
    const response = await apiClient(api, endpoint, 'POST', sanitizedRequest);
    return response as LoadsVsNonAsnChartResponse;
  } catch (error) {
    console.error('Error fetching ASN Loads vs Non-ASN Loads chart:', error);
    throw error;
  }
}

// ─── Location Details — Companion endpoint ────────────────────────────────────

/**
 * Request body for the ASN Gap "Location Summary" companion table.
 *
 * POST /asn/location-details/companion
 *
 * Scopes results to the counterpart locations that have exchanged loads with
 * the selected anchor row. `scopeKeys` lists which grain dimensions are pinned
 * (a subset of `grainSelections` containing only geo keys: region/market/
 * location), and `scopeVals` carries the anchor row's values for those keys.
 */
export interface ASNLocationDetailsCompanionRequest extends ASNOverviewKpiRequest {
  perspective: 'shipping' | 'receiving' | '';
  scopeKeys: AsnLocationGrainKey[];
  scopeVals: Partial<Record<AsnLocationGrainKey, string>>;
  /** Column key to sort by — a grain key (region/market/location) or metric
   * key (nonAsnLoads/totalLoads/pctNonAsn/avgSkus/caseQty). */
  sortBy?: string;
  sortDirection?: 'ASC' | 'DESC';
  /** Zero-based page index. Optional because the backend does not yet honor
   * pagination on this endpoint — see ASNLocationDetailsCompanionPagination. */
  pageNumber?: number;
  pageSize?: number;
}

/**
 * A single row of the companion table — one counterpart location that has
 * exchanged loads with the selected anchor row.
 *
 * The API returns a flat shape: metric fields are direct properties of the row,
 * not nested inside a `vals` object. The interface matches the actual payload:
 *   { location, region, market, nonAsnLoads, totalLoads, avgSkus, caseQty }
 * No `key` or `pctNonAsn` field is returned — `location` is the unique row
 * identifier and `pctNonAsn` is derived client-side from nonAsnLoads/totalLoads.
 */
export interface ASNLocationDetailsCompanionRow {
  location: string;
  region: string;
  market: string;
  nonAsnLoads: number;
  totalLoads: number;
  avgSkus: number;
  caseQty: number;
}

export interface ASNLocationDetailsCompanionGrandTotal {
  locationCount: number;
  nonAsnLoads: number;
  totalLoads: number;
  pctNonAsn: number;
  avgSkus: number;
  caseQty: number;
}

/**
 * The backend does not yet paginate this endpoint server-side — only
 * `totalRowCount` is guaranteed. `totalPages`/`pageIndex`/`pageSize`/`hasMore`
 * are optional so the UI can detect and prefer true server pagination the
 * moment the backend adds it, falling back to client-side paging until then.
 */
export interface ASNLocationDetailsCompanionPagination {
  totalRowCount: number;
  totalPages?: number;
  pageIndex?: number;
  pageSize?: number;
  hasMore?: boolean;
}

export interface ASNLocationDetailsCompanionResponse {
  rows: ASNLocationDetailsCompanionRow[];
  grandTotal: ASNLocationDetailsCompanionGrandTotal | null;
  pagination?: ASNLocationDetailsCompanionPagination;
  executionTimeMs?: number;
  /** Present when `rows` is empty — render instead of a blank companion table. */
  message?: string;
}

/**
 * Fetch the companion table for a selected Location Summary row.
 *
 * POST /asn/location-details/companion
 *
 * Returns the counterpart locations (e.g. receiving locations linked to a
 * selected shipping location) that have exchanged loads with the anchor row
 * under the currently applied global filters.
 *
 * @param request - Global filter criteria + perspective/scopeKeys/scopeVals.
 * @returns Typed ASNLocationDetailsCompanionResponse (companion rows).
 */
export async function fetchAsnLocationDetailsCompanion(
  request: ASNLocationDetailsCompanionRequest,
): Promise<ASNLocationDetailsCompanionResponse> {
  const api = createApiClient();

  const sanitizedRequest: ASNLocationDetailsCompanionRequest = {
    shippingRegions: sanitizeStringArray(request.shippingRegions),
    shippingMarkets: sanitizeStringArray(request.shippingMarkets),
    shippingLocationIds: sanitizeStringArray(request.shippingLocationIds),
    receivingLocationIds: sanitizeStringArray(request.receivingLocationIds),
    years: sanitizeNumberArray(request.years),
    periods: sanitizeNumberArray(request.periods),
    weeks: sanitizeNumberArray(request.weeks),
    dateFrom: request.dateFrom ?? '',
    dateTo: request.dateTo ?? '',
    ...sanitizeCaseQuantityRange(request),
    perspective: request.perspective || '',
    scopeKeys: Array.isArray(request.scopeKeys) ? request.scopeKeys : [],
    scopeVals: request.scopeVals ?? {},
    sortBy: request.sortBy || 'nonAsnLoads',
    sortDirection: request.sortDirection || 'DESC',
    pageNumber:
      Number.isFinite(request.pageNumber) && (request.pageNumber as number) >= 0
        ? request.pageNumber
        : 0,
    pageSize:
      Number.isFinite(request.pageSize) && (request.pageSize as number) > 0
        ? request.pageSize
        : 200,
  };

  const endpoint = '/asn/location-details/companion';

  try {
    const response = await apiClient(api, endpoint, 'POST', sanitizedRequest);
    return response as ASNLocationDetailsCompanionResponse;
  } catch (error) {
    console.error('Error fetching ASN Gap Location Details companion:', error);
    throw error;
  }
}
